/**
 * Pole formuláře editoru zájezdu → záložka, kde ho registruje soubor záložky (`register("…")`,
 * `name="…"` u `Controller`). Zvýrazní záložky s chybou a po neúspěšném uložení přepne na první.
 * Chyba serveru přijde pod jménem sloupce, proto jsou tu i sloupce bez vlastního pole
 * (`countryCodes`, `hotelLatitude`/`hotelLongitude`). `upgradeIds` váže stránka (výběr je
 * v záložce Obsah). `highlights` vede na Zážitek; hotel, který zážitek není, má pole v záložce
 * Hotel, ale chyba volných textů bodů je vzácná a `resolveEditorTab` ji pošle na první záložku.
 * Přeměření: `rg -o '(register\(|name=)"[a-zA-Z]+' apps/admin/src/components/trips/<Tab>.tsx`.
 */
export const TRIP_FIELD_TO_TAB: Record<string, string> = {
  // Základní (BasicInfoTab, OnRequestBookingFields; `status` v hlavičce stránky)
  title: "basic",
  slug: "basic",
  destinationId: "basic",
  destinationTagIds: "basic",
  guideId: "basic",
  guideVideoUrl: "basic",
  difficultyLevel: "basic",
  tripKind: "basic",
  fulfillmentMode: "basic",
  tripType: "basic",
  countryCodesText: "basic",
  countryCodes: "basic",
  tripStyle: "basic",
  priceFrom: "basic",
  priceIsFrom: "basic",
  priceOriginal: "basic",
  durationHours: "basic",
  durationDays: "basic",
  leadTimeHours: "basic",
  availableWeekdays: "basic",
  groupSizeMin: "basic",
  groupSizeMax: "basic",
  groupSizeOpenEnded: "basic",
  defaultMeetingPoint: "basic",
  defaultMeetingTime: "basic",
  heroTitle: "basic",
  heading: "basic",
  shortDescription: "basic",
  description: "basic",
  metaTitle: "basic",
  metaDescription: "basic",
  featuredImageId: "basic",
  isFeatured: "basic",
  isLastMinute: "basic",
  isForWomen: "basic",
  hasCzechGuide: "basic",
  tags: "basic",
  status: "basic",
  // Itinerář
  itinerary: "itinerary",
  // Zážitek
  highlights: "experience",
  schedule: "experience",
  // Hotel
  hotelStars: "hotel",
  hotelRooms: "hotel",
  hotelMealPlans: "hotel",
  hotelDepartures: "hotel",
  hotelFeatures: "hotel",
  hotelLatitude: "hotel",
  hotelLongitude: "hotel",
  hotelCoordinatesText: "hotel",
  // V ceně
  included: "included",
  excluded: "included",
  optionalActivities: "included",
  note: "included",
  // Galerie
  galleryImageIds: "gallery",
  youtubeVideos: "gallery",
  // Termíny
  dates: "dates",
  // Kusovník
  components: "components",
  // Obsah
  faqs: "content",
  socialReels: "content",
  mapImageId: "content",
  guideRecommendation: "content",
  relatedItems: "content",
  relatedPageIds: "content",
  upgradeIds: "content",
};

/**
 * Pole formuláře editoru destinace → záložka, kde ho registruje komponenta záložky
 * (`register("…")`, `name="…"` u `Controller`; `heroTitle`/`heading` váže `BasicTab` přes `register`).
 * `featuredImageId` je v `BasicTab` i v `MediaTab`: vede na první záložku, kde pole je.
 * Pole, která žádná záložka neregistruje (`featuredImage`, `additionalImage`, `galleryImages`),
 * se nemapují: záložka by chybu stejně neukázala, zůstane `ErrorBanner`.
 * Přeměření: `rg -o '(register\(|name=)"[a-zA-Z]+' apps/admin/src/components/destinations/<Tab>.tsx`.
 */
export const DESTINATION_FIELD_TO_TAB: Record<string, string> = {
  // Základní (BasicTab)
  name: "basic",
  slug: "basic",
  continent: "basic",
  parentId: "basic",
  parentInfoText: "basic",
  guideId: "basic",
  latitude: "basic",
  longitude: "basic",
  shortDescription: "basic",
  description: "basic",
  bestTimeToVisit: "basic",
  climateInfo: "basic",
  heroTitle: "basic",
  heading: "basic",
  featuredImageId: "basic",
  metaTitle: "basic",
  metaDescription: "basic",
  sortOrder: "basic",
  isFeatured: "basic",
  isPublished: "basic",
  showInNav: "basic",
  showAsCountry: "basic",
  defaultMinPax: "basic",
  autoRouteLeads: "basic",
  // Obsah (ContentTab)
  additionalContent: "content",
  additionalImageId: "content",
  highlights: "content",
  regions: "content",
  testimonial: "content",
  // Praktické (PracticalTab)
  practicalInfo: "practical",
  weatherData: "practical",
  // Kuchyně (CuisineTab)
  cuisine: "cuisine",
  activities: "cuisine",
  whyWithUs: "cuisine",
  // Média (MediaTab)
  galleryImageIds: "media",
  heroVideoUrl: "media",
  moreVideosUrl: "media",
  socialReels: "media",
  videoPromo: "media",
  // FAQ (FaqTab)
  faqs: "faq",
  // Nabídka (OfferTab)
  offerContent: "offer",
  planningBlockVariant: "offer",
  planningBlockText: "offer",
  // Související (RelatedTab)
  inspirationIds: "related",
};

/**
 * Pole formuláře editoru inspirace → záložka (stejné pravidlo jako u destinace). `status` je
 * v hlavičce editoru, mimo záložky; `tipsContent` a `testimonialAuthorImage` žádná záložka
 * neregistruje. Přeměření nad `apps/admin/src/components/activities/<Tab>.tsx`.
 */
export const ACTIVITY_FIELD_TO_TAB: Record<string, string> = {
  // Základní (BasicInfoTab)
  title: "basic",
  slug: "basic",
  activityType: "basic",
  difficultyLevel: "basic",
  duration: "basic",
  destinationId: "basic",
  location: "basic",
  featuredImageId: "basic",
  shortDescription: "basic",
  heroTitle: "basic",
  heading: "basic",
  isFeatured: "basic",
  sortOrder: "basic",
  metaTitle: "basic",
  metaDescription: "basic",
  // Popis (DescriptionTab)
  description: "description",
  // Galerie (GalleryTab)
  galleryImageIds: "gallery",
  // V ceně a štítky (IncludedTagsTab)
  included: "included",
  tags: "included",
  // Videa (VideosTab)
  videos: "videos",
  socialReels: "videos",
  // Tipy (TipsTab)
  tips: "tips",
  testimonialQuote: "tips",
  testimonialAuthor: "tips",
  testimonialAuthorImageId: "tips",
  // Související (RelatedTab)
  relatedItems: "related",
};

/**
 * Záložka pole podle kořene cesty (`dates.0.price` → `dates`). Vlastní klíče mapy, ne zděděné:
 * chyba pole se jménem jako `constructor` by jinak „našla" funkci z prototypu objektu.
 */
function tabOfField(fieldPath: string, mapping: Record<string, string>): string | null {
  const root = fieldPath.split(".")[0];
  return Object.hasOwn(mapping, root) ? mapping[root] : null;
}

/**
 * Returns the first tab ID that has any validation error, or null.
 * Used for auto-switching to errored tab on submit failure. `errors` je cokoli s klíči podle
 * cest polí: `FieldErrors` formuláře i `ApiError.fieldErrors` serverové chyby.
 */
export function getFirstTabWithErrors(
  errors: Record<string, unknown>,
  mapping: Record<string, string>,
): string | null {
  for (const fieldPath of Object.keys(errors)) {
    const tab = tabOfField(fieldPath, mapping);
    if (tab) return tab;
  }
  return null;
}

/**
 * Returns set of tab IDs that have any validation error.
 * Used for rendering red dot indicators on tab nav.
 */
export function getTabsWithErrors(
  errors: Record<string, unknown>,
  mapping: Record<string, string>,
): Set<string> {
  const tabs = new Set<string>();
  for (const fieldPath of Object.keys(errors)) {
    const tab = tabOfField(fieldPath, mapping);
    if (tab) tabs.add(tab);
  }
  return tabs;
}
